import React, { useEffect, useState } from "react";
import dayjs from "dayjs";
import "./Scss/InformesGStyles.scss";

import {
  columnsTableContacts,
  columnsTableClients,
  columnsTableAsocZone,
} from "./Components/Tables";

import { CasesLineChart, CasesPieCharts } from "./Components/Charts";
import { useUser } from "../Components/Context/UserProvider";
import { CommonSubHeader } from "../Components/helpers/Helpers";
import Table from "../Components/Common/Table/Table";
import LoadingPage from "../Components/Common/Loading/LoadingPage";

import {
  Tabs,
  Tab,
  Box,
  Fade,
  Grid,
  TextField,
  MenuItem,
  Button,
  Paper,
  Typography,
  LinearProgress,
} from "@mui/material";

import api from "../api/axios";
import ReactPaginate from "react-paginate";
import { ChevronLeft, ChevronRight } from "lucide-react";

/* ------------------------------ */
/* Helpers                        */
/* ------------------------------ */

const TabPanel = ({ children, value, index }) =>
  value === index && <Box sx={{ p: 2 }}>{children}</Box>;

const KpiCard = ({ title, value, color = "#1976d2" }) => (
  <Paper sx={{ p: 2, backgroundColor: color + "20" }}>
    <Typography variant="subtitle2" color={color}>
      {title}
    </Typography>
    <Typography variant="h5" fontWeight="bold" color={color}>
      {value}
    </Typography>
  </Paper>
);

const TableWithPagination = ({
  columns,
  data,
  pagination,
  loading,
  onPageChange,
}) => {
  if (loading) return <LoadingPage isLoading heightProp="40vh" />;

  return (
    <Fade in timeout={400}>
      <div className="dashboardContainers">
        <Table columns={columns} data={data} />
        {pagination.pageCount > 1 && (
          <div className="tablePaginateContainer">
            <ReactPaginate
              breakLabel="..."
              nextLabel={<ChevronRight size={18} />}
              previousLabel={<ChevronLeft size={18} />}
              onPageChange={onPageChange}
              pageRangeDisplayed={3}
              marginPagesDisplayed={2}
              pageCount={pagination.pageCount}
              forcePage={pagination.page - 1}
              containerClassName="pagination"
              activeClassName="active"
            />
          </div>
        )}
      </div>
    </Fade>
  );
};

/* ------------------------------ */
/* Componente principal           */
/* ------------------------------ */

export default function InformeGeneral() {
  const { userData } = useUser();
  const { user } = userData;

  /* ------------------------------ */
  /* Estados                        */
  /* ------------------------------ */

  const [tab, setTab] = useState(0);

  const [contactsData, setContactsData] = useState([]);
  const [clientsData, setClientsData] = useState([]);
  const [asocZoneData, setAsocZoneData] = useState([]);

  const [casesGraph, setCasesGraph] = useState([]);
  const [casesStatusData, setCasesStatusData] = useState([]);
  const [casesStatusOthers, setCasesStatusOthers] = useState([]);

  const [loading, setLoading] = useState({
    contacts: false,
    clients: false,
    asocZone: false,
    cases: false,
  });

  const [pagination, setPagination] = useState({
    contacts: { page: 1, pageCount: 1 },
    clients: { page: 1, pageCount: 1 },
    asocZone: { page: 1, pageCount: 1 },
  });

  /* ------------------------------ */
  /* Filtros                        */
  /* ------------------------------ */

  const [dateFromState, setDateFromState] = useState(
    dayjs().subtract(1, "year").format("YYYY-MM-DD"),
  );
  const [dateToState, setDateToState] = useState(dayjs().format("YYYY-MM-DD"));

  const [company, setCompany] = useState({
    id: 0,
    company_name: "Seleccionar",
  });

  const companiesList = [{ id: 0, company_name: "Seleccionar" }];

  /* ------------------------------ */
  /* Fetch helpers                  */
  /* ------------------------------ */

  const postFetch = async (url, page, setter, loadingKey, paginationKey) => {
    try {
      setLoading((p) => ({ ...p, [loadingKey]: true }));

      const res = await api.post(url, {
        page,
        dateFrom: dateFromState + " 00:00:00",
        dateTo: dateToState + " 23:59:59",
        companyId: company.id,
      });

      console.log(res);

      const payload = res.data;

      // CONTACTS
      if (payload.contacts) {
        setter(payload.contacts.data ?? []);
        setPagination((p) => ({
          ...p,
          [paginationKey]: {
            page: payload.contacts.current_page,
            pageCount: payload.contacts.last_page,
          },
        }));
        return;
      }

      // CLIENTS
      if (payload.ClientsMonthAsoc) {
        setter(payload.ClientsMonthAsoc.data ?? []);
        setPagination((p) => ({
          ...p,
          [paginationKey]: {
            page: payload.ClientsMonthAsoc.current_page,
            pageCount: payload.ClientsMonthAsoc.last_page,
          },
        }));
        return;
      }

      // CASES ASOC ZONE
      if (payload.CasesAsocZone) {
        setter(payload.CasesAsocZone.data ?? []);
        setPagination((p) => ({
          ...p,
          [paginationKey]: {
            page: payload.CasesAsocZone.current_page,
            pageCount: payload.CasesAsocZone.last_page,
          },
        }));
        return;
      }

      setter([]);
    } finally {
      setLoading((p) => ({ ...p, [loadingKey]: false }));
    }
  };

  /* ------------------------------ */
  /* Fetch gráficos                 */
  /* ------------------------------ */

  const fetchCasesGraph = async () => {
    const res = await api.post(`${process.env.REACT_APP_API_URL}/graphcases`, {
      dateFrom: dateFromState + " 00:00:00",
      dateTo: dateToState + " 23:59:59",
      companyId: company.id,
    });

    console.log(res);

    const rows = res.data?.data ?? [];

    setCasesGraph(
      rows.map((i) => ({
        date: i.date,
        count: i.count,
      })),
    );
  };

  const fetchCasesByStatus = async (retry = 0) => {
    try {
      setLoading((p) => ({ ...p, cases: true }));

      const res = await api.post(
        `${process.env.REACT_APP_API_URL}/casesbystatus`,
        {
          dateFrom: dateFromState + " 00:00:00",
          dateTo: dateToState + " 23:59:59",
          companyId: company.id,
        },
      );

      console.log(res);

      if (res.status === 202) {
        if (retry < 5) {
          setTimeout(() => fetchCasesByStatus(retry + 1), 2000);
        }
        return;
      }

      const raw = res.data?.data ?? [];
      if (!raw.length) return;

      const total = raw.reduce((a, b) => a + Number(b.total), 0);

      const formatted = raw.map((i) => ({
        label: `Estado ${i.status}`,
        value: Number(((i.total / total) * 100).toFixed(2)),
      }));

      setCasesStatusData(formatted);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading((p) => ({ ...p, cases: false }));
    }
  };

  /* ------------------------------ */
  /* Effects                        */
  /* ------------------------------ */
  useEffect(() => {
    if (!user) return;

    postFetch(
      `${process.env.REACT_APP_API_URL}/contactstable`,
      pagination.contacts.page,
      setContactsData,
      "contacts",
      "contacts",
    );

    postFetch(
      `${process.env.REACT_APP_API_URL}/clientsmonthasoc`,
      pagination.clients.page,
      setClientsData,
      "clients",
      "clients",
    );

    postFetch(
      `${process.env.REACT_APP_API_URL}/casesasoczone`,
      pagination.asocZone.page,
      setAsocZoneData,
      "asocZone",
      "asocZone",
    );
  }, [
    user,
    pagination.contacts.page,
    pagination.clients.page,
    pagination.asocZone.page,
    dateFromState,
    dateToState,
    company.id,
  ]);

  useEffect(() => {
    if (!user) return;

    fetchCasesGraph();
  }, [user, dateFromState, dateToState, company.id]);

  useEffect(() => {
    if (!user) return;

    fetchCasesByStatus();
  }, [user, dateFromState, dateToState, company.id]);

  /* ------------------------------ */
  /* KPIs                           */
  /* ------------------------------ */

  const totalContacts = contactsData.length;
  const totalClients = clientsData.length;
  const totalCases = casesGraph.reduce((a, b) => a + b.count, 0);
  const avgCases =
    casesGraph.length > 0 ? Math.round(totalCases / casesGraph.length) : 0;

  /* ------------------------------ */
  /* Render                         */
  /* ------------------------------ */

  return (
    <div className="fatherContainer">
      <CommonSubHeader title="Informe General" />

      {/* Filtros */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={3}>
            <TextField
              label="Desde"
              type="date"
              fullWidth
              value={dateFromState}
              onChange={(e) => setDateFromState(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              label="Hasta"
              type="date"
              fullWidth
              value={dateToState}
              onChange={(e) => setDateToState(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              select
              label="Empresa"
              fullWidth
              value={company.id}
              onChange={(e) =>
                setCompany(
                  companiesList.find((c) => c.id === Number(e.target.value)),
                )
              }>
              {companiesList.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.company_name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={3}>
            <Button
              fullWidth
              variant="contained"
              onClick={() => {
                // solo forzamos rerender cambiando estado
                setPagination((p) => ({
                  contacts: { ...p.contacts, page: 1 },
                  clients: { ...p.clients, page: 1 },
                  asocZone: { ...p.asocZone, page: 1 },
                }));
              }}>
              Aplicar filtros
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* KPIs */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={12} md={3}>
          <KpiCard title="Contactos" value={totalContacts} />
        </Grid>
        <Grid item xs={12} md={3}>
          <KpiCard title="Clientes" value={totalClients} color="#2e7d32" />
        </Grid>
        <Grid item xs={12} md={3}>
          <KpiCard title="Casos" value={totalCases} color="#d32f2f" />
        </Grid>
        <Grid item xs={12} md={3}>
          <KpiCard
            title="Promedio Casos / Mes"
            value={avgCases}
            color="#6a1b9a"
          />
        </Grid>
      </Grid>

      {/* Tabs */}
      <Tabs value={tab} onChange={(e, v) => setTab(v)} centered>
        <Tab label="Contactos" />
        <Tab label="Clientes" />
        <Tab label="Expedientes" />
      </Tabs>

      <TabPanel value={tab} index={0}>
        <Fade in timeout={400}>
          <div className="dashboardContainers">
            <Table
              columns={columnsTableContacts}
              data={contactsData}
              view="contactos"
              viewTitle="contactos"
              apiRoute="contacts"
              checkBoxFlag={false}
            />

            {pagination.contacts.pageCount > 1 && (
              <div className="tablePaginateContainer">
                <ReactPaginate
                  breakLabel="..."
                  nextLabel={<ChevronRight size={18} />}
                  previousLabel={<ChevronLeft size={18} />}
                  onPageChange={(e) =>
                    setPagination((p) => ({
                      ...p,
                      contacts: { ...p.contacts, page: e.selected + 1 },
                    }))
                  }
                  pageRangeDisplayed={3}
                  marginPagesDisplayed={2}
                  pageCount={pagination.contacts.pageCount}
                  forcePage={pagination.contacts.page - 1}
                  containerClassName="pagination"
                  activeClassName="active"
                />
              </div>
            )}
          </div>
        </Fade>
      </TabPanel>

      <TabPanel value={tab} index={1}>
        <Fade in timeout={400}>
          <div className="dashboardContainers">
            <Table
              columns={columnsTableClients}
              data={clientsData}
              view="clientes"
              viewTitle="clientes"
              apiRoute="clients"
              checkBoxFlag={false}
            />

            {pagination.clients.pageCount > 1 && (
              <div className="tablePaginateContainer">
                <ReactPaginate
                  breakLabel="..."
                  nextLabel={<ChevronRight size={18} />}
                  previousLabel={<ChevronLeft size={18} />}
                  onPageChange={(e) =>
                    setPagination((p) => ({
                      ...p,
                      clients: { ...p.clients, page: e.selected + 1 },
                    }))
                  }
                  pageRangeDisplayed={3}
                  marginPagesDisplayed={2}
                  pageCount={pagination.clients.pageCount}
                  forcePage={pagination.clients.page - 1}
                  containerClassName="pagination"
                  activeClassName="active"
                />
              </div>
            )}
          </div>
        </Fade>
      </TabPanel>

      <TabPanel value={tab} index={2}>
        {loading.cases && <LinearProgress />}

        <Grid container spacing={2}>
          <Grid item xs={12} md={7}>
            <Fade in timeout={400}>
              <div className="dashboardContainers">
                <Table
                  columns={columnsTableAsocZone}
                  data={asocZoneData}
                  view="expedientes"
                  viewTitle="expedientes"
                  apiRoute="cases"
                  checkBoxFlag={false}
                />

                {pagination.asocZone.pageCount > 1 && (
                  <div className="tablePaginateContainer">
                    <ReactPaginate
                      breakLabel="..."
                      nextLabel={<ChevronRight size={18} />}
                      previousLabel={<ChevronLeft size={18} />}
                      onPageChange={(e) =>
                        setPagination((p) => ({
                          ...p,
                          asocZone: { ...p.asocZone, page: e.selected + 1 },
                        }))
                      }
                      pageRangeDisplayed={3}
                      marginPagesDisplayed={2}
                      pageCount={pagination.asocZone.pageCount}
                      forcePage={pagination.asocZone.page - 1}
                      containerClassName="pagination"
                      activeClassName="active"
                    />
                  </div>
                )}
              </div>
            </Fade>
          </Grid>

          <Grid item xs={12} md={5}>
            <CasesLineChart casesGraph={casesGraph} />
            <CasesPieCharts casesStatusData={casesStatusData} />
          </Grid>
        </Grid>
      </TabPanel>
    </div>
  );
}
